import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { CALCS_PER_PAGE, CALC_NAMES, CALC_PAGE_TYPES } from 'utils/calculatorUtils';

import { LoaderOn, Loader } from 'tt-ui-lib/core';
import CalcAccordion from '../../components/CalcAccordion/CalcAccordion';
import CalcIcon from '../../components/CalcIcon/CalcIcon';
import CalcFilter from '../../components/CalcFilter/CalcFilter';
import GhgDraft from '../../components/ByType/GhgReport/DraftListElement/ReportDraft';
import CustomDraft from '../../components/ByType/CustomReport/DraftListElement/ReportDraft';
import CalcEmptyListBlock from '../../components/CalcEmptyListBlock/CalcEmptyListBlock';
import { Alert, Snackbar } from '@mui/material';
import { getReportDraftWithFilter } from 'api/reports/endpoints/reports';
import { useSelectedContext } from 'context/contextSelector';

const ReportDrafts = ({ targetWidth, pageType }) => {
  const types = CALCS_PER_PAGE[pageType];
  const { pathname } = useLocation();

  const [searchedDrafts, setSearchedDrafts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [needReload, setNeedReload] = useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedCompany: widjetCompany } = useSelectedContext((context) => context.user);

  const onErrorClose = () => {
    setErrorText(null);
  };

  const RenewReportDraftsList = () => {
    setLoading(true);
    getReportDraftWithFilter({ pageType })
      .then((res) => {
        if (res.data.success) {
          const { data } = res.data;
          setDrafts(data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        setNeedReload(false);
      });
  };

  useEffect(() => {
    if (!needReload) return;
    RenewReportDraftsList();
  }, [needReload]);

  useEffect(() => {
    RenewReportDraftsList();
  }, [pathname]);

  const companyDrafts =
    widjetCompany && widjetCompany.selectedCompany
      ? drafts.filter((el) => el.companyId === widjetCompany.selectedCompany.id)
      : drafts;

  return (
    <>
      {companyDrafts.length > 0 ? (
        <>
          <CalcFilter setSearchedItems={setSearchedDrafts} items={companyDrafts} />
          {types.map((type) => (
            <>
              {searchedDrafts.some((d) => d.type === type) && (
                <div key={type} style={{ position: 'relative' }}>
                  <CalcAccordion logo={<CalcIcon type={type} />} title={CALC_NAMES[type]}>
                    {searchedDrafts
                      .filter((draft) => draft.type === type && draft.periodReportDate)
                      .sort((a, b) => new Date(b.periodReportDate) - new Date(a.periodReportDate))
                      .map((draft) => (
                        <div key={draft.id}>
                          {pageType === CALC_PAGE_TYPES.CUSTOM && (
                            <CustomDraft
                              targetWidth={targetWidth}
                              draft={draft}
                              refresh={() => setNeedReload(true)}
                              setErrorText={setErrorText}
                            />
                          )}
                          {pageType === CALC_PAGE_TYPES.SUSTAINABILITY && (
                            <GhgDraft
                              targetWidth={targetWidth}
                              draft={draft}
                              refresh={() => setNeedReload(true)}
                              setErrorText={setErrorText}
                            />
                          )}
                        </div>
                      ))}
                  </CalcAccordion>
                  {needReload && <LoaderOn />}
                </div>
              )}
            </>
          ))}
        </>
      ) : loading ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Loader />
        </div>
      ) : (
        <CalcEmptyListBlock collectionName="drafts" />
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!errorText}
        onClose={onErrorClose}
      >
        <Alert
          onClose={onErrorClose}
          severity="error"
          sx={{ width: '100%', backgroundColor: '#D32F2F', color: '#ffffff' }}
        >
          {errorText ?? 'Something went wrong, try again'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportDrafts;
